import { useEffect } from "react";

const DEFAULT_SITE_URL = "https://www.ageofcompanies.com";
const DEFAULT_IMAGE = "/logo-small.png";

const ensureMeta = (selector, attributes) => {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([name, value]) => element.setAttribute(name, value));
  return element;
};

const ensureLink = (rel) => {
  let element = document.head.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", rel);
    document.head.appendChild(element);
  }
  return element;
};

export default function Seo({
  title,
  description,
  path,
  image = DEFAULT_IMAGE,
  noIndex = false,
  structuredData,
}) {
  useEffect(() => {
    const siteUrl = (process.env.REACT_APP_SITE_URL || DEFAULT_SITE_URL).replace(/\/$/, "");
    const canonicalUrl = path ? `${siteUrl}${path === "/" ? "/" : path}` : null;
    const imageUrl = image.startsWith("http") ? image : `${siteUrl}${image}`;
    const shouldIndex = !noIndex && process.env.REACT_APP_ENVIRONMENT !== "test";

    if (title) document.title = title;
    if (description) ensureMeta('meta[name="description"]', { name: "description", content: description });
    ensureMeta('meta[name="robots"]', {
      name: "robots",
      content: shouldIndex ? "index, follow, max-image-preview:large" : "noindex, nofollow",
    });

    if (canonicalUrl) {
      ensureLink("canonical").setAttribute("href", canonicalUrl);
      ensureMeta('meta[property="og:url"]', { property: "og:url", content: canonicalUrl });
    } else {
      document.head.querySelector('link[rel="canonical"]')?.remove();
      document.head.querySelector('meta[property="og:url"]')?.remove();
    }

    ensureMeta('meta[property="og:site_name"]', { property: "og:site_name", content: "Age of Companies" });
    ensureMeta('meta[property="og:type"]', { property: "og:type", content: "website" });
    if (title) {
      ensureMeta('meta[property="og:title"]', { property: "og:title", content: title });
      ensureMeta('meta[name="twitter:title"]', { name: "twitter:title", content: title });
    }
    if (description) {
      ensureMeta('meta[property="og:description"]', { property: "og:description", content: description });
      ensureMeta('meta[name="twitter:description"]', { name: "twitter:description", content: description });
    }
    ensureMeta('meta[property="og:image"]', { property: "og:image", content: imageUrl });
    ensureMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    ensureMeta('meta[name="twitter:image"]', { name: "twitter:image", content: imageUrl });

    let script = document.head.querySelector('script[data-seo-structured-data="true"]');
    if (structuredData) {
      if (!script) {
        script = document.createElement("script");
        script.type = "application/ld+json";
        script.dataset.seoStructuredData = "true";
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(structuredData);
    } else {
      script?.remove();
    }
  }, [description, image, noIndex, path, structuredData, title]);

  return null;
}
